import { signUp } from '@aws-amplify/auth';
import { Button, Divider, Paper, TextField, Typography } from '@mui/material';
import { emailRegex } from 'components/Drawer/variants/externalMembers/shared';
import useCurrentTenant from 'hooks/useCurrentTenant/useCurrentTenant';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useGoBackOneOnboardingStep from 'hooks/useGoBackOneOnboardingStep/useGoBackOneOnboardingStep';
import React, { useEffect, useState } from 'react';
import PasswordChecklist from 'react-password-checklist';
import { setCurrentTenantAction } from 'state/actions/currentTenant';
import { currentTenantVar } from 'state/vars';
import Background from 'ui/atoms/Background/Background';
import ComponentWrapper from '../ComponentWrapper/ComponentWrapper';

export interface Props {
  tenantId?: string;
}

const CreateAccountPage: React.FC<React.PropsWithChildren<Props>> = ({ tenantId }) => {
  const [userName, setUserName] = useState('');
  const [email, setEmailLocal] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });

  const { getCurrentTenant } = useCurrentTenant({
    handleFetchError
  });

  const { goBackOneOnboardingStep, loading: goBackOneOnboardingStepLoading } = useGoBackOneOnboardingStep({
    handleFetchError
  });

  const onBackButtonClick = () => {
    if (!goBackOneOnboardingStepLoading) goBackOneOnboardingStep();
  };

  useEffect(() => {
    setIsEmailValid(!!email.toLowerCase().match(emailRegex));
    setShowValidation(false);
  }, [email]);

  const isNextButtonDisabled = () =>
    !Boolean(userName !== '' && email !== '' && isEmailValid && password !== '' && isPasswordValid);

  const handleCreateAccount = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (userName && email && password && tenantId) {
      event.preventDefault();
      try {
        setIsLoading(true);
        const data = await signUp({
          username: email,
          password,
          options: {
            userAttributes: {
              email: email,
              name: userName,
              'custom:tenantId': tenantId
            }
          }
        });

        if (data) {
          setIsLoading(false);
          const currentTenant = currentTenantVar();
          if (currentTenant?.name)
            setCurrentTenantAction({ ...currentTenant, onboardingStep: 'VerifyAccount', additionalData: email });

          getCurrentTenant();
        }
      } catch (err) {
        setIsLoading(false);
        enqueueSnackbar(err as string, { snackbartype: 'error' });
      }
    }
  };

  return (
    <ComponentWrapper width="100vw" height="100vh" alignItems="center" justifyContent="center">
      <Background id="background" />
      <Paper
        elevation={3}
        data-testid="login-window"
        id="login-window"
        style={{
          alignSelf: 'center',
          justifySelf: 'center',
          padding: '48px 80px',
          width: '33vw',
          borderRadius: '10px'
        }}
      >
        <ComponentWrapper flexDirection="column" gap="16px">
          <Typography variant="h4">Create administrator account</Typography>
          <Divider />
          <form>
            <ComponentWrapper flexDirection="column" gap="16px">
              <TextField label="Full name" value={userName} onChange={(event) => setUserName(event.target.value)} />
              <TextField
                type="email"
                label="Email address"
                value={email}
                onChange={(event) => {
                  setEmailLocal(event.target.value);
                }}
                onBlur={() => setShowValidation(true)}
                error={showValidation && !isEmailValid}
                helperText={showValidation && !isEmailValid ? 'Please enter a valid email' : ''}
              />
              <TextField
                type="password"
                label="Password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </ComponentWrapper>
          </form>
          <Typography variant="body2">Password requirements</Typography>
          <ComponentWrapper flexDirection="column" padding="0 8px" gap="2px">
            <PasswordChecklist
              rules={['minLength', 'specialChar', 'number', 'capital', 'lowercase']}
              minLength={8}
              value={password}
              onChange={(isValid) => {
                setIsPasswordValid(isValid);
              }}
              messages={{
                minLength: 'At least 8 characters',
                specialChar: 'A special character',
                number: 'A number',
                capital: 'A capital letter',
                lowercase: 'A lowercase letter'
              }}
            />
          </ComponentWrapper>
          <Divider />
          <ComponentWrapper justifyContent="space-between">
            <Button onClick={onBackButtonClick} variant="contained" size="large" color="secondary">
              Back
            </Button>
            <Button
              onClick={handleCreateAccount}
              disabled={isNextButtonDisabled()}
              variant="contained"
              size="large"
              color="primary"
              loading={isLoading}
            >
              Next
            </Button>
          </ComponentWrapper>
        </ComponentWrapper>
      </Paper>
    </ComponentWrapper>
  );
};

export default CreateAccountPage;
